import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { ArrowRight, ArrowLeft, CheckCircle, Loader2, Wrench, Droplet, Zap, HardHat } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Trade-specific branching content
const TRADES = [
  { id: 'HVAC', label: 'HVAC', icon: Wrench, sub: 'Heating, Cooling, Refrigeration' },
  { id: 'Plumbing', label: 'Plumbing', icon: Droplet, sub: 'Residential & Commercial' },
  { id: 'Electrical', label: 'Electrical', icon: Zap, sub: 'Residential & Commercial' },
  { id: 'Other Trade', label: 'Other Trade', icon: HardHat, sub: 'Roofing, Garage, Landscaping, etc.' },
];

const CREW_OPTIONS = {
  HVAC: ['Just me (owner/operator)', '2-5 techs', '6-15 techs', '16-40 techs', '40+ techs'],
  Plumbing: ['Just me (owner/operator)', '2-5 plumbers', '6-15 plumbers', '16-40 plumbers', '40+ plumbers'],
  Electrical: ['Just me (owner/operator)', '2-5 electricians', '6-15 electricians', '16-40 electricians', '40+ electricians'],
  'Other Trade': ['Just me (owner/operator)', '2-5 crew', '6-15 crew', '16-40 crew', '40+ crew'],
};

const PAIN_OPTIONS = {
  HVAC: [
    'Missed emergency calls on nights/weekends',
    'AC install quote follow-ups fall through',
    'Maintenance plan renewals not converting',
    'Dispatch/scheduling chaos between techs',
    'Customers bounce because we don\'t answer fast enough',
  ],
  Plumbing: [
    'Missed emergency leaks after hours',
    'Drain/sewer jobs we never called back',
    'Estimate follow-ups go cold',
    'Scheduling conflicts between plumbers',
    'Customers bounce to the next plumber on Google',
  ],
  Electrical: [
    'Missed service calls after 5pm',
    'Panel upgrade estimates never get signed',
    'Permit/inspection coordination drops',
    'Scheduling conflicts between electricians',
    'Customers bounce because we don\'t answer fast enough',
  ],
  'Other Trade': [
    'Missed calls outside business hours',
    'Estimate follow-ups go cold',
    'Scheduling conflicts between crew',
    'Leads ghost after first contact',
    'We can\'t answer fast enough to compete',
  ],
};

const AFTER_HOURS_OPTIONS = [
  'Voicemail → we call back next day',
  'Third-party answering service',
  'Owner\'s personal cell phone',
  'I try to answer every call myself',
  'Nothing - we just miss them',
];

export const QuizModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    businessCategory: '', // Stores selected TRADE
    annualRevenue: '',
    leastEfficient: '', // Stores trade-specific pain point
    teamSize: '', // Stores trade-specific crew size
    timelineUrgency: '',
    currentAIUsage: '', // Stores after-hours handling answer
  });

  const totalSteps = 4;

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTradeSelect = (trade) => {
    // Reset downstream selections when trade changes
    setFormData((prev) => ({
      ...prev,
      businessCategory: trade,
      leastEfficient: '',
      teamSize: '',
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/quiz/submit`, formData);
      console.log('Quiz submitted successfully:', response.data);
      setIsSubmitting(false);
      setIsComplete(true);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      setIsSubmitting(false);
      setIsComplete(true);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setIsComplete(false);
    setFormData({
      fullName: '',
      email: '',
      company: '',
      businessCategory: '',
      annualRevenue: '',
      leastEfficient: '',
      teamSize: '',
      timelineUrgency: '',
      currentAIUsage: '',
    });
    onClose();
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return !!formData.businessCategory && !!formData.teamSize;
      case 2:
        return !!formData.annualRevenue && !!formData.leastEfficient;
      case 3:
        return !!formData.currentAIUsage && !!formData.timelineUrgency;
      case 4:
        return !!formData.fullName && !!formData.email && !!formData.company;
      default:
        return false;
    }
  };

  const trade = formData.businessCategory || 'Other Trade';
  const painOptions = PAIN_OPTIONS[trade] || PAIN_OPTIONS['Other Trade'];
  const crewOptions = CREW_OPTIONS[trade] || CREW_OPTIONS['Other Trade'];

  if (isComplete) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          className="bg-[#0a0a0a] border border-[#00E599]/30 text-white max-w-lg"
          data-testid="quiz-complete-dialog"
        >
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-[#00E599]/15 border border-[#00E599]/40 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-[#00E599]" />
            </div>
            <h2 className="text-3xl font-headline font-bold mb-4">You're On The List.</h2>
            <p className="text-[#A1A1AA] mb-6 leading-relaxed font-body">
              We'll review your answers and send your personalized missed-revenue report to your inbox within 24 hours. Keep an eye out for a text from{' '}
              <span className="text-[#00E599] font-semibold">(602) 730-6385</span>.
            </p>
            <Button
              onClick={handleClose}
              className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] font-bold"
              data-testid="quiz-complete-close-btn"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="bg-[#0a0a0a] border border-[#00E599]/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="quiz-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-headline font-bold">
            See how many jobs you're missing.
          </DialogTitle>
          <p className="text-[#A1A1AA] text-sm font-body">
            Step {currentStep} of {totalSteps} • 60-second trade audit
          </p>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="w-full bg-zinc-800 h-2 rounded-full overflow-hidden mb-6">
          <div
            className="h-full bg-gradient-to-r from-[#00E599] to-[#00C985] transition-all duration-300"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          ></div>
        </div>

        {/* Step 1: Trade + Crew Size */}
        {currentStep === 1 && (
          <div className="space-y-6" data-testid="quiz-step-1">
            <h3 className="text-xl font-headline font-semibold text-[#00E599]">
              What trade are you in?
            </h3>

            <div className="grid grid-cols-2 gap-3">
              {TRADES.map((t) => {
                const Icon = t.icon;
                const selected = formData.businessCategory === t.id;
                return (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => handleTradeSelect(t.id)}
                    data-testid={`quiz-trade-${t.id.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`text-left p-4 rounded-xl border transition-all duration-200 ${
                      selected
                        ? 'bg-[#00E599]/15 border-[#00E599] shadow-lg shadow-[#00E599]/20'
                        : 'bg-[#050505] border-zinc-800 hover:border-[#00E599]/40'
                    }`}
                  >
                    <Icon className={`w-6 h-6 mb-2 ${selected ? 'text-[#00E599]' : 'text-[#A1A1AA]'}`} />
                    <div className="font-body font-semibold text-white">{t.label}</div>
                    <div className="text-xs text-[#A1A1AA] mt-1">{t.sub}</div>
                  </button>
                );
              })}
            </div>

            {formData.businessCategory && (
              <div className="space-y-3">
                <Label className="text-white">How big is your crew? *</Label>
                <RadioGroup
                  value={formData.teamSize}
                  onValueChange={(value) => handleInputChange('teamSize', value)}
                >
                  {crewOptions.map((size) => (
                    <div
                      key={size}
                      className={`flex items-center space-x-2 border rounded-lg p-3 transition-all duration-200 cursor-pointer ${
                        formData.teamSize === size
                          ? 'bg-[#00E599]/15 border-[#00E599]'
                          : 'bg-[#050505] border-zinc-800 hover:border-[#00E599]/30'
                      }`}
                    >
                      <RadioGroupItem value={size} id={size} data-testid={`quiz-crew-option`} />
                      <Label htmlFor={size} className="cursor-pointer flex-1 font-body">
                        {size}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Revenue + Pain Point (trade-specific) */}
        {currentStep === 2 && (
          <div className="space-y-6" data-testid="quiz-step-2">
            <h3 className="text-xl font-headline font-semibold text-[#00E599]">
              Where are you losing jobs?
            </h3>

            <div className="space-y-3">
              <Label className="text-white">Annual revenue *</Label>
              <RadioGroup
                value={formData.annualRevenue}
                onValueChange={(value) => handleInputChange('annualRevenue', value)}
              >
                {[
                  'Under $500K',
                  '$500K - $1M',
                  '$1M - $3M',
                  '$3M - $10M',
                  '$10M - $25M',
                  '$25M+',
                  'Prefer not to say',
                ].map((revenue) => (
                  <div
                    key={revenue}
                    className={`flex items-center space-x-2 border rounded-lg p-3 transition-all duration-200 cursor-pointer ${
                      formData.annualRevenue === revenue
                        ? 'bg-[#00E599]/15 border-[#00E599]'
                        : 'bg-[#050505] border-zinc-800 hover:border-[#00E599]/30'
                    }`}
                  >
                    <RadioGroupItem value={revenue} id={`rev-${revenue}`} />
                    <Label htmlFor={`rev-${revenue}`} className="cursor-pointer flex-1 font-body">
                      {revenue}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-3">
              <Label className="text-white">
                What's costing you the most jobs right now? <span className="text-[#00E599]">({trade})</span> *
              </Label>
              <RadioGroup
                value={formData.leastEfficient}
                onValueChange={(value) => handleInputChange('leastEfficient', value)}
              >
                {painOptions.map((pain) => (
                  <div
                    key={pain}
                    className={`flex items-center space-x-2 border rounded-lg p-3 transition-all duration-200 cursor-pointer ${
                      formData.leastEfficient === pain
                        ? 'bg-[#00E599]/15 border-[#00E599]'
                        : 'bg-[#050505] border-zinc-800 hover:border-[#00E599]/30'
                    }`}
                  >
                    <RadioGroupItem value={pain} id={`pain-${pain}`} />
                    <Label htmlFor={`pain-${pain}`} className="cursor-pointer flex-1 font-body">
                      {pain}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        )}

        {/* Step 3: After-hours handling + Timeline */}
        {currentStep === 3 && (
          <div className="space-y-6" data-testid="quiz-step-3">
            <h3 className="text-xl font-headline font-semibold text-[#00E599]">
              How do you handle calls today?
            </h3>

            <div className="space-y-3">
              <Label className="text-white">Current after-hours call handling *</Label>
              <RadioGroup
                value={formData.currentAIUsage}
                onValueChange={(value) => handleInputChange('currentAIUsage', value)}
              >
                {AFTER_HOURS_OPTIONS.map((option) => (
                  <div
                    key={option}
                    className={`flex items-center space-x-2 border rounded-lg p-3 transition-all duration-200 cursor-pointer ${
                      formData.currentAIUsage === option
                        ? 'bg-[#00E599]/15 border-[#00E599]'
                        : 'bg-[#050505] border-zinc-800 hover:border-[#00E599]/30'
                    }`}
                  >
                    <RadioGroupItem value={option} id={`ah-${option}`} />
                    <Label htmlFor={`ah-${option}`} className="cursor-pointer flex-1 font-body">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-3">
              <Label className="text-white">When do you want this live? *</Label>
              <RadioGroup
                value={formData.timelineUrgency}
                onValueChange={(value) => handleInputChange('timelineUrgency', value)}
              >
                {[
                  'ASAP - bleeding money every week',
                  'This month',
                  'Next 60 days',
                  'Just exploring for now',
                ].map((timeline) => (
                  <div
                    key={timeline}
                    className={`flex items-center space-x-2 border rounded-lg p-3 transition-all duration-200 cursor-pointer ${
                      formData.timelineUrgency === timeline
                        ? 'bg-[#00E599]/15 border-[#00E599]'
                        : 'bg-[#050505] border-zinc-800 hover:border-[#00E599]/30'
                    }`}
                  >
                    <RadioGroupItem value={timeline} id={`tl-${timeline}`} />
                    <Label htmlFor={`tl-${timeline}`} className="cursor-pointer flex-1 font-body">
                      {timeline}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        )}

        {/* Step 4: Contact */}
        {currentStep === 4 && (
          <div className="space-y-6" data-testid="quiz-step-4">
            <h3 className="text-xl font-headline font-semibold text-[#00E599]">
              Where do we send your missed-revenue report?
            </h3>
            <p className="text-[#A1A1AA] text-sm font-body">
              We'll send your personalized breakdown — no spam, no sales traps.
            </p>

            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-white">Full name *</Label>
              <Input
                id="fullName"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className="bg-[#050505] border-zinc-800 text-white"
                placeholder="John Smith"
                data-testid="quiz-fullname-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="bg-[#050505] border-zinc-800 text-white"
                placeholder="you@company.com"
                data-testid="quiz-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company" className="text-white">Company name *</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                className="bg-[#050505] border-zinc-800 text-white"
                placeholder="Smith HVAC & Plumbing"
                data-testid="quiz-company-input"
              />
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center pt-6 border-t border-zinc-800 mt-8">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900 disabled:opacity-40"
            data-testid="quiz-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentStep < totalSteps ? (
            <Button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] font-bold disabled:opacity-40"
              data-testid="quiz-next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!isStepValid() || isSubmitting}
              className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] font-bold disabled:opacity-40"
              data-testid="quiz-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Get My Report
                  <CheckCircle className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuizModal;
